import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  ExpenseDocument,
  ExpensePagination,
  ExpenseSummary,
} from '../../api/backofficeExpenses';

export type AnnualExpensePoint = {
  month: string; // e.g. 'Jan', 'Feb'
  totalsByType: Record<string, number>; // dynamic map of expense type -> value
  total: number;
};

export type CategoryExpensePoint = {
  name: string;
  amount: number;
};

export interface ExpensesSliceObject {
  // Legacy monthly totals — kept for backward compat (dashboard, summary screens)
  monthly: {
    payments: number;
    stocks: number;
    others: number;
    loading: boolean;
    error?: string | null;
  };
  // New: rich summary with counts + previous period comparison
  summary: {
    data: ExpenseSummary | null;
    loading: boolean;
    error: string | null;
  };
  // New: paginated expense list
  list: {
    expenses: ExpenseDocument[];
    pagination: ExpensePagination | null;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
  };
  // New: single expense detail
  detail: {
    data: ExpenseDocument | null;
    loading: boolean;
    error: string | null;
  };
  annual: {
    points: AnnualExpensePoint[];
    loading: boolean;
    error?: string | null;
  };
  categories: {
    items: CategoryExpensePoint[];
    loading: boolean;
    error?: string | null;
  };
  types: {
    items: string[];
    loading: boolean;
    error?: string | null;
  };
}

const initialState: ExpensesSliceObject = {
  monthly: { payments: 0, stocks: 0, others: 0, loading: false, error: null },
  summary: { data: null, loading: false, error: null },
  list: {
    expenses: [],
    pagination: null,
    loading: false,
    loadingMore: false,
    error: null,
  },
  detail: { data: null, loading: false, error: null },
  annual: { points: [], loading: false, error: null },
  categories: { items: [], loading: false, error: null },
  types: { items: [], loading: false, error: null },
};

const expensesSlice = createSlice({
  name: 'backofficeExpenses',
  initialState,
  reducers: {
    // ─── Legacy monthly (kept for backward compat) ──────────────────
    setMonthlyLoading: (state, action: PayloadAction<boolean>) => {
      state.monthly.loading = action.payload;
    },
    setMonthlyError: (state, action: PayloadAction<string | null>) => {
      state.monthly.error = action.payload;
    },
    setMonthlyData: (
      state,
      action: PayloadAction<{
        payments: number;
        stocks: number;
        others: number;
      }>,
    ) => {
      state.monthly.payments = Number(action.payload.payments || 0);
      state.monthly.stocks = Number(action.payload.stocks || 0);
      state.monthly.others = Number(action.payload.others || 0);
    },

    // ─── Summary ────────────────────────────────────────────────────
    setSummaryLoading: (state, action: PayloadAction<boolean>) => {
      state.summary.loading = action.payload;
    },
    setSummaryError: (state, action: PayloadAction<string | null>) => {
      state.summary.error = action.payload;
    },
    setSummaryData: (state, action: PayloadAction<ExpenseSummary | null>) => {
      state.summary.data = action.payload;
    },

    // ─── Expense list ───────────────────────────────────────────────
    setListLoading: (state, action: PayloadAction<boolean>) => {
      state.list.loading = action.payload;
    },
    setListLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.list.loadingMore = action.payload;
    },
    setListError: (state, action: PayloadAction<string | null>) => {
      state.list.error = action.payload;
    },
    setListData: (
      state,
      action: PayloadAction<{
        expenses: ExpenseDocument[];
        pagination: ExpensePagination | null;
      }>,
    ) => {
      state.list.expenses = action.payload.expenses;
      state.list.pagination = action.payload.pagination;
    },
    appendListData: (
      state,
      action: PayloadAction<{
        expenses: ExpenseDocument[];
        pagination: ExpensePagination | null;
      }>,
    ) => {
      state.list.expenses = [...state.list.expenses, ...action.payload.expenses];
      state.list.pagination = action.payload.pagination;
    },
    resetExpensesList: (state) => {
      state.list.expenses = [];
      state.list.pagination = null;
      state.list.loading = false;
      state.list.loadingMore = false;
      state.list.error = null;
    },

    // ─── Expense detail ─────────────────────────────────────────────
    setDetailLoading: (state, action: PayloadAction<boolean>) => {
      state.detail.loading = action.payload;
    },
    setDetailError: (state, action: PayloadAction<string | null>) => {
      state.detail.error = action.payload;
    },
    setDetailData: (state, action: PayloadAction<ExpenseDocument | null>) => {
      state.detail.data = action.payload;
    },

    // ─── Annual ─────────────────────────────────────────────────────
    setAnnualLoading: (state, action: PayloadAction<boolean>) => {
      state.annual.loading = action.payload;
    },
    setAnnualError: (state, action: PayloadAction<string | null>) => {
      state.annual.error = action.payload;
    },
    setAnnualPoints: (state, action: PayloadAction<AnnualExpensePoint[]>) => {
      state.annual.points = action.payload;
    },

    // ─── Categories ─────────────────────────────────────────────────
    setCategoryLoading: (state, action: PayloadAction<boolean>) => {
      state.categories.loading = action.payload;
    },
    setCategoryError: (state, action: PayloadAction<string | null>) => {
      state.categories.error = action.payload;
    },
    setCategoryItems: (
      state,
      action: PayloadAction<CategoryExpensePoint[]>,
    ) => {
      state.categories.items = action.payload;
    },

    // ─── Expense types ──────────────────────────────────────────────
    setExpenseTypesLoading: (state, action: PayloadAction<boolean>) => {
      state.types.loading = action.payload;
    },
    setExpenseTypesError: (state, action: PayloadAction<string | null>) => {
      state.types.error = action.payload;
    },
    setExpenseTypes: (state, action: PayloadAction<string[]>) => {
      state.types.items = action.payload;
    },
  },
});

export const {
  // Legacy monthly
  setMonthlyLoading,
  setMonthlyError,
  setMonthlyData,
  // Summary
  setSummaryLoading,
  setSummaryError,
  setSummaryData,
  // List
  setListLoading,
  setListLoadingMore,
  setListError,
  setListData,
  appendListData,
  resetExpensesList,
  // Detail
  setDetailLoading,
  setDetailError,
  setDetailData,
  // Annual
  setAnnualLoading,
  setAnnualError,
  setAnnualPoints,
  // Categories
  setCategoryLoading,
  setCategoryError,
  setCategoryItems,
  // Expense types
  setExpenseTypesLoading,
  setExpenseTypesError,
  setExpenseTypes,
} = expensesSlice.actions;

export default expensesSlice.reducer;
